<script setup lang="ts">
/**
 * BannerFinalCta
 *
 * End-of-page call to action.
 * Opens intent modal for contact-related links instead of navigating.
 * Optional background image with overlay.
 */

import type { BannerFinalCtaProps } from "~~/shared/types";
const props = defineProps<BannerFinalCtaProps>();

const { resolveLink } = useServiceRoutes();
const { open: openIntentModal, resolveQuoteIntent, handleLink } = useIntentModal();
const { trackCtaClick, trackPhoneClick } = useAnalytics();

// Get the resolved href for the button
const buttonHref = computed(() => resolveLink(props.button));

/**
 * Check if the link should open the intent modal
 * Matches /contact, /contact/*, /contact?*, and /quote
 */
function shouldOpenModal(href: string): boolean {
  return href.startsWith("/contact") || href === "/quote";
}

/**
 * Derive intent from button text using keyword matching
 */
function deriveIntentFromButtonText(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("consultation")) return "consultation";
  if (lower.includes("assessment")) return "assessment";
  if (lower.includes("support") || lower.includes("ticket")) return "support";
  if (lower.includes("quote")) return "free-quote";
  return "free-quote"; // Default fallback
}

function handleButtonClick() {
  trackCtaClick(props.button.text, 'final_cta', buttonHref.value);
  // If the button href carries an explicit intent (e.g. /contact?need=cctv-sydney),
  // route through handleLink so the registered intent fires verbatim. Fall back to
  // the button-text-derived intent when no explicit need= is present.
  if (handleLink(buttonHref.value)) {
    return;
  }
  if (shouldOpenModal(buttonHref.value)) {
    openIntentModal(resolveQuoteIntent(deriveIntentFromButtonText(props.button.text), props.service));
  } else {
    navigateTo(buttonHref.value);
  }
}

function handlePhoneClick() {
  trackPhoneClick('final_cta');
}
</script>

<template>
  <section class="relative overflow-hidden bg-primary-600 py-20 sm:py-24">
    <!-- Background Image (optional) -->
    <div v-if="backgroundImage" class="absolute inset-0">
      <ResponsiveImage
        :src="backgroundImage"
        :alt="title"
        class="size-full object-cover opacity-20"
        :width="1920"
        :height="1080"
      />
      <div class="absolute inset-0 bg-primary-600/80"></div>
    </div>

    <!-- Content -->
    <div class="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
      <h2
        class="mb-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl text-balance"
      >
        {{ title }}
      </h2>

      <p
        v-if="description"
        class="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-primary-100"
      >
        {{ description }}
      </p>

      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-full bg-mw-accent px-10 py-4 text-lg font-bold text-slate-900 transition-all duration-300 hover:bg-mw-accent-400 hover:shadow-xl"
        @click="handleButtonClick"
      >
        {{ button.text }}
        <Icon name="lucide:arrow-right" size="22" />
      </button>

      <!-- Trust Line -->
      <p class="mt-8 text-sm text-primary-200">
        Or call us directly at
        <LocalPhone
          class="font-semibold text-white underline-offset-2 hover:underline"
          @click="handlePhoneClick"
        />
      </p>
    </div>
  </section>
</template>
